import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { CheckCircle2, CircleAlert, MessageCircle } from "lucide-react";
import { Button } from "@/components/atoms/Button";
import { cn } from "@/lib/cn";
import type { LabelReachoutPreview } from "@/services/labelsService";

interface Props {
  open: boolean;
  preview: LabelReachoutPreview | null;
  submitting?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

const ISSUE_LABELS: Record<string, string> = {
  CHANNEL_NOT_FOUND: "No WhatsApp channel",
  PHONE_REQUIRED: "Missing phone number",
  CONTACT_ARCHIVED: "Archived",
  MARKETING_CONSENT_REVOKED: "Marketing consent revoked",
};

function issueLabel(code: string): string {
  return ISSUE_LABELS[code] ?? code.replace(/_/g, " ").toLowerCase();
}

/**
 * Dry-run of whether labelled contacts can be messaged, shown before a campaign
 * is created. Nothing is sent from this screen.
 */
export function CampaignReachoutPreviewModal({
  open,
  preview,
  submitting = false,
  onCancel,
  onConfirm,
}: Props) {
  const [showBlocked, setShowBlocked] = useState(true);
  const issueEntries = useMemo(
    () => Object.entries(preview?.issue_counts ?? {}).sort((a, b) => b[1] - a[1]),
    [preview],
  );

  if (!open || !preview) return null;

  const allReady = preview.all_ready || preview.blocked === 0;
  const noneReady = preview.ready === 0;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/45"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onCancel();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="campaign-reachout-preview-title"
        className="bg-white rounded-2xl shadow-2xl w-[calc(100vw-2rem)] max-w-5xl overflow-hidden ring-1 ring-slate-500/10"
      >
        <div
          className={cn(
            "flex items-start gap-3 px-5 py-4 border-b",
            allReady ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50",
          )}
        >
          <div
            className={cn(
              "w-9 h-9 rounded-full flex items-center justify-center shrink-0",
              allReady ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700",
            )}
          >
            {allReady ? <CheckCircle2 className="w-5 h-5" /> : <CircleAlert className="w-5 h-5" />}
          </div>
          <div className="min-w-0">
            <h2 id="campaign-reachout-preview-title" className="text-base font-bold text-slate-900">
              Can we message these people?
            </h2>
            <p className={cn("text-xs mt-0.5", allReady ? "text-emerald-800" : "text-amber-800")}>
              {allReady
                ? `All ${preview.total} contact${preview.total === 1 ? "" : "s"} on the selected labels can receive ${preview.channel_type || "WhatsApp"}.`
                : `${preview.ready} of ${preview.total} can receive ${preview.channel_type || "WhatsApp"}. ${preview.blocked} would be skipped.`}
            </p>
          </div>
        </div>

        <div className="px-6 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-3 gap-2">
            <Stat label="Total" value={preview.total} />
            <Stat label="Ready" value={preview.ready} tone="success" />
            <Stat label="Blocked" value={preview.blocked} tone={preview.blocked > 0 ? "warning" : "muted"} />
          </div>

          {issueEntries.length > 0 && (
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-bridge-muted mb-2">
                Why contacts are blocked
              </p>
              <ul className="divide-y divide-bridge-border/60 rounded-lg border border-bridge-border/60">
                {issueEntries.map(([code, count]) => (
                  <li key={code} className="flex items-center justify-between px-3 py-2 gap-3">
                    <span className="text-sm text-slate-700">{issueLabel(code)}</span>
                    <span className="tabular-nums text-sm font-semibold text-slate-800">{count}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {preview.blocked_contacts.length > 0 && (
            <div>
              <button
                type="button"
                onClick={() => setShowBlocked((open) => !open)}
                className="text-xs font-semibold text-bridge-accent hover:underline"
              >
                {showBlocked ? "Hide" : "Show"} blocked contacts ({preview.blocked_contacts.length})
              </button>
              {showBlocked && (
                <ul className="mt-2 space-y-2">
                  {preview.blocked_contacts.map((contact) => (
                    <li
                      key={contact.contact_id}
                      className="rounded-lg border border-amber-100 bg-amber-50/60 px-3 py-2"
                    >
                      <p className="text-sm font-medium text-slate-800">
                        {contact.name?.trim() || contact.contact_id}
                      </p>
                      <ul className="mt-1 space-y-0.5">
                        {contact.issues.map((issue, index) => (
                          <li key={`${issue.code}-${index}`} className="text-xs text-amber-900">
                            <span className="font-semibold">{issueLabel(issue.code)}.</span>{" "}
                            {issue.message?.trim() || ""}
                          </li>
                        ))}
                      </ul>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {noneReady && (
            <p className="text-sm text-amber-800">
              Nobody on these labels can be messaged yet. You can still create the campaign, but
              the first send will have no recipients until these issues are fixed.
            </p>
          )}
        </div>

        <div className="px-5 py-4 border-t border-bridge-border/60 bg-slate-50 flex items-center justify-end gap-2">
          <Button variant="secondary" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="primary" loading={submitting} disabled={submitting} onClick={onConfirm}>
            <MessageCircle size={14} />
            Create campaign
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function Stat({
  label,
  value,
  tone = "muted",
}: {
  label: string;
  value: number;
  tone?: "success" | "warning" | "muted";
}) {
  return (
    <div className="rounded-xl border border-bridge-border/60 px-3 py-2.5">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-bridge-muted">{label}</p>
      <p
        className={cn(
          "mt-0.5 text-xl font-bold tabular-nums",
          tone === "success" && "text-emerald-700",
          tone === "warning" && "text-amber-700",
          tone === "muted" && "text-slate-800",
        )}
      >
        {value}
      </p>
    </div>
  );
}
